import { ToastContext, type ToastContextType } from "./ToastContextBase";
import React, { useState, useCallback } from "react";
import { ToastContainer } from "../components/ui/Toast";
import type { Toast } from "../components/ui/Toast";

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = generateId();
      const newToast: Toast = {
        id,
        duration: 5000, // Default 5 seconds
        ...toast,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [generateId],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const showSuccess = useCallback(
    (title: string, message?: string, options?: Partial<Toast>) => {
      return addToast({
        type: "success",
        title,
        message,
        ...options,
      });
    },
    [addToast],
  );

  const showError = useCallback(
    (title: string, message?: string, options?: Partial<Toast>) => {
      return addToast({
        type: "error",
        title,
        message,
        duration: 8000, // Errors stay longer by default
        ...options,
      });
    },
    [addToast],
  );

  const showWarning = useCallback(
    (title: string, message?: string, options?: Partial<Toast>) => {
      return addToast({
        type: "warning",
        title,
        message,
        duration: 6000, // Warnings stay a bit longer
        ...options,
      });
    },
    [addToast],
  );

  const showInfo = useCallback(
    (title: string, message?: string, options?: Partial<Toast>) => {
      return addToast({
        type: "info",
        title,
        message,
        ...options,
      });
    },
    [addToast],
  );

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};
